/**
 * Browser: same-origin (works on phone via LAN IP).
 * Next.js rewrites /api and /health → local API on this machine.
 * Server-side: hit API directly.
 */
export function getApiBase(): string {
  if (typeof window !== "undefined") {
    return "";
  }
  return (process.env.NEXT_PUBLIC_API_URL ?? "http://127.0.0.1:4000").replace(
    /\/$/,
    "",
  );
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit & { timeoutMs?: number },
): Promise<T> {
  const base = getApiBase();
  const url = path.startsWith("http")
    ? path
    : `${base}${path.startsWith("/") ? path : `/${path}`}`;

  const { timeoutMs, ...rest } = init ?? {};
  const controller = new AbortController();
  const parentSignal = rest.signal;
  let timedOut = false;

  const onParentAbort = () => controller.abort();
  if (parentSignal) {
    if (parentSignal.aborted) controller.abort();
    else parentSignal.addEventListener("abort", onParentAbort, { once: true });
  }

  const timer =
    timeoutMs && timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs)
      : null;

  try {
    const res = await fetch(url, {
      ...rest,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(rest.headers ?? {}),
      },
    });

    if (!res.ok) {
      const text = await res.text();
      // Prefer human message from API JSON: { ok:false, message:"..." }
      try {
        const j = JSON.parse(text) as { message?: string; error?: string };
        const msg = j.message || j.error;
        if (msg && typeof msg === "string") throw new Error(msg);
      } catch (e) {
        if (e instanceof Error && e.message && !e.message.startsWith("{")) {
          throw e;
        }
      }
      throw new Error(text || `Request failed: ${res.status}`);
    }

    return res.json() as Promise<T>;
  } catch (e) {
    if (timedOut) {
      throw new Error(
        "That took too long. The local AI model may be busy — try again, or use a smaller Ollama model.",
      );
    }
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
    if (parentSignal) parentSignal.removeEventListener("abort", onParentAbort);
  }
}
